import { User } from 'firebase/auth'
import {
  UploadStaging,
  DriverMatchResult,
  NormalizedDataRow,
  ProcessingProgress
} from '@/types'
import { FirestoreService } from './firestoreService'
import { DriverMasterService } from './driverMasterService'

/**
 * Upload Processing Service
 *
 * Handles the processing of staged upload data:
 * 1. Driver matching from normalized data
 * 2. Updates staging with driver matches
 * 3. Prepares data for earnings processing
 */

export interface ProcessingResult {
  stagingId: string
  totalRows: number
  processedRows: number
  matchedDrivers: number
  newDriversNeeded: number
  driverMatches: DriverMatchResult[]
  errors: Array<{
    rowIndex: number
    error: string
  }>
}

export class UploadProcessingService {
  private user: User
  private firestoreService: FirestoreService

  constructor(user: User) {
    this.user = user
    this.firestoreService = new FirestoreService(user)
  }

  /**
   * Process staged upload data to match drivers
   */
  async processUploadStaging(
    stagingId: string,
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<ProcessingResult> {
    const startTime = Date.now()

    try {
      // Get staging data
      const staging = await this.firestoreService.getUploadStaging(stagingId)
      if (!staging) {
        throw new Error('Upload staging not found')
      }

      if (!staging.normalizedData) {
        staging.normalizedData = []
      }

      // Update staging status to processing
      await this.firestoreService.updateUploadStaging(stagingId, {
        status: 'processing'
      })

      const totalRows = staging.normalizedData.length
      const driverMatches: DriverMatchResult[] = []
      const errors: Array<{ rowIndex: number; error: string }> = []

      // Process each normalized data row
      for (let i = 0; i < staging.normalizedData.length; i++) {
        const normalizedRow = staging.normalizedData[i]

        // Update progress
        const percentage = Math.round(((i + 1) / totalRows) * 100)
        if (onProgress) {
          onProgress({
            uploadId: staging.uploadId,
            currentBatch: i + 1,
            totalBatches: totalRows,
            processedRows: i + 1,
            totalRows,
            percentage,
            currentStatus: `Matchar förare ${i + 1} av ${totalRows}`,
            startTime: new Date(startTime),
            estimatedTimeRemaining: this.calculateEstimatedTime(startTime, i + 1, totalRows)
          })
        }

        try {
          // Extract driver match from normalized row
          const driverMatch = await this.processDriverMatch(
            normalizedRow,
            staging.platformInfo.detectedPlatform || 'other'
          )
          driverMatches.push(driverMatch)
        } catch (error) {
          console.error(`[UploadProcessing] Error processing row ${normalizedRow.rowIndex}:`, error)
          errors.push({
            rowIndex: normalizedRow.rowIndex,
            error: error instanceof Error ? error.message : 'Unknown processing error'
          })
        }

        // Small delay to avoid overwhelming the system
        if (i % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 10))
        }
      }

      // Update staging with driver matches
      await this.firestoreService.updateUploadStaging(stagingId, {
        driverMatches,
        status: 'processed'
      })

      const matchedDrivers = driverMatches.filter(match => match.hasMatch).length
      const newDriversNeeded = driverMatches.filter(match => !match.hasMatch && match.identifiers.name).length

      const result = {
        stagingId,
        totalRows,
        processedRows: driverMatches.length,
        matchedDrivers,
        newDriversNeeded,
        driverMatches,
        errors
      }

      return result

    } catch (error) {
      console.error('[UploadProcessing] Processing failed:', error)

      // Update staging with error
      await this.firestoreService.updateUploadStaging(stagingId, {
        status: 'failed',
        error: error instanceof Error ? error.message : 'Processing failed'
      })

      throw error
    }
  }

  /**
   * Process driver matching for a single normalized row
   */
  private async processDriverMatch(
    normalizedRow: NormalizedDataRow,
    platform: string
  ): Promise<DriverMatchResult> {
    const { driverIdentifiers } = normalizedRow.extractedData

    // Search for driver match
    const match = await DriverMasterService.searchDriversAliases(
      this.user,
      platform,
      {
        uuid: driverIdentifiers.uuid || undefined,
        name: driverIdentifiers.name || undefined,
        email: driverIdentifiers.email || undefined,
        phone: driverIdentifiers.phone || undefined
      }
    )

    // Create driver match result
    const driverMatch: DriverMatchResult = {
      rowIndex: normalizedRow.rowIndex,
      extractedName: driverIdentifiers.name || undefined,
      identifiers: driverIdentifiers,
      hasMatch: match !== null,
      aliasExists: match?.aliasExists || false,
      needsAlias: match?.needsAlias || false,
      fuzzyMatch: match?.fuzzyMatch || false
    }

    // Add match details if found
    if (match) {
      driverMatch.matchedDriverId = match.driverId
      driverMatch.matchedDriverName = match.driverName
      // IMPORTANT: searchDriversAliases returns matchType as the identifier used (uuid/name/email/phone)
      // We map this to matchedVia for the upload workflow
      driverMatch.matchedVia = match.matchType  // uuid | name | email | phone
      driverMatch.matchedValue = match.matchedValue
      driverMatch.matchScore = match.matchScore
      // Set matchType based on whether it was fuzzy or exact
      driverMatch.matchType = match.fuzzyMatch ? 'fuzzy' : 'exact'
    }

    return driverMatch
  }

  /**
   * Get processed staging data for earnings creation
   */
  async getProcessedStaging(stagingId: string): Promise<UploadStaging | null> {
    const staging = await this.firestoreService.getUploadStaging(stagingId)

    if (!staging || staging.status !== 'processed' || !staging.driverMatches) {
      return null
    }

    return staging
  }

  /**
   * Get staging by upload ID
   */
  async getStagingByUploadId(uploadId: string): Promise<UploadStaging | null> {
    return this.firestoreService.getUploadStagingByUploadId(uploadId)
  }

  /**
   * Calculate estimated time remaining
   */
  private calculateEstimatedTime(startTime: number, completed: number, total: number): number {
    const elapsed = Date.now() - startTime
    const avgTimePerItem = elapsed / completed
    const remaining = total - completed
    return Math.round((avgTimePerItem * remaining) / 1000) // Return seconds
  }

  /**
   * Retry processing for failed staging
   */
  async retryProcessing(
    stagingId: string,
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<ProcessingResult> {
    // Reset error state
    await this.firestoreService.updateUploadStaging(stagingId, {
      status: 'pending',
      error: undefined,
      driverMatches: undefined
    })

    return this.processUploadStaging(stagingId, onProgress)
  }
}